// apps/web/src/self-service/queries.ts — Phase 4 plan 04-04.
//
// GraphQL operations for the self-service surface (A8 / A9 / A10).
// Mirrors the 04-03 wizard queries (`wizard/queries.ts`): plain
// `graphql-request` `gql` template literals dispatched through the
// shared `gqlRequest` wrapper. The raw token is held in
// `lib/api.ts`'s in-memory store (D-02 / T-04-32) and injected
// as the `x-self-service-token` request header on every call.
//
// Per the plan's two-source `OUTSIDE_SAME_DAY_CUTOFF` (D-13) the
// server's resolver already supplies `extensions.source: "selfService"`
// on the self-service path; the web client reads
// `extensions.messageKey` first and falls back to a code-based map
// in `wizard/validation.ts`. We don't add a new key path here —
// `mapErrorCode` already routes `OUTSIDE_SAME_DAY_CUTOFF` to
// `errors.outsideSameDayCutoffSelfService` when `source === "selfService"`
// (the new i18n key added in 04-04 — see `i18n/de.ts`).

import { gql } from "graphql-request";

/**
 * SELF-01 — read-only reservation lookup by token. The response
 * shape includes `isCancellable` (server-computed, D-09) so the
 * A8 page can render the action buttons without knowing the
 * 60-minute window formula. The 04-02 server returns the public-
 * safe surface: contact fields are redacted (D-09 / T-04-14) for
 * the read path; modify + cancel populate them because the caller
 * is the authenticated owner.
 */
export const GET_RESERVATION_BY_TOKEN = gql`
  query GetReservationByToken($token: String!) {
    reservationByToken(token: $token) {
      id
      reservationNumber
      status
      partySize
      serviceDate
      slotDefinitionId
      startsAt
      durationMinutes
      areaId
      confirmationMode
      createdAt
      consentAt
      isCancellable
    }
  }
`;

/**
 * SELF-02 — modify the reservation. The response mirrors the read
 * shape but the server includes the contact fields (the caller
 * proved ownership via the Argon2id-verified token).
 */
export const MODIFY_RESERVATION_BY_TOKEN = gql`
  mutation ModifyReservationByToken($input: ModifyReservationByTokenInput!) {
    modifyReservationByToken(input: $input) {
      id
      reservationNumber
      status
      partySize
      serviceDate
      slotDefinitionId
      startsAt
      durationMinutes
      areaId
      confirmationMode
      createdAt
      consentAt
      isCancellable
    }
  }
`;

/**
 * SELF-03 — cancel the reservation. The response includes
 * `isCancellable: false` because the row is now in `storniert`
 * status (the server updates the row before returning).
 */
export const CANCEL_RESERVATION_BY_TOKEN = gql`
  mutation CancelReservationByToken($input: CancelReservationByTokenInput!) {
    cancelReservationByToken(input: $input) {
      id
      reservationNumber
      status
      partySize
      serviceDate
      slotDefinitionId
      startsAt
      durationMinutes
      areaId
      confirmationMode
      createdAt
      consentAt
      isCancellable
    }
  }
`;
